import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import { toast } from "react-toastify";
import io from "socket.io-client";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    height: "100%",
    overflow: "auto",
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  connectionStatus: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: "50%",
  },
  connected: {
    backgroundColor: theme.palette.success.main,
  },
  disconnected: {
    backgroundColor: theme.palette.error.main,
  },
  reconnecting: {
    backgroundColor: theme.palette.warning.main,
  },
  metricCard: {
    padding: theme.spacing(2),
    height: "100%",
  },
  chartCard: {
    padding: theme.spacing(2),
    height: "400px",
  },
}));

const ServerMonitoring = () => {
  const classes = useStyles();
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Historical data for charts (last 60 data points = 2 minutes)
  const [metricsHistory, setMetricsHistory] = useState([]);
  const maxDataPoints = 60;

  // Initialize Socket.IO connection
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("No autenticado");
      setLoading(false);
      return;
    }

    // Parse token to get user data
    let parsedToken = token;
    try {
      parsedToken = JSON.parse(token);
    } catch (e) {
      // Token is already a string
    }

    // Get user ID from localStorage or API
    const getUserId = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:8080"}/auth/me`, {
          headers: {
            Authorization: `Bearer ${parsedToken}`,
          },
        });
        const data = await response.json();
        return data.user?.id || data.id || 1;
      } catch (error) {
        console.error("Error getting user ID:", error);
        return 1; // Fallback ID
      }
    };

    // Create Socket.IO connection
    const initSocket = async () => {
      const userId = await getUserId();
      
      // CRÍTICO: Conectar al namespace /monitoring
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
      const monitoringUrl = `${backendUrl}/monitoring`;
      
      console.log("🔌 Conectando a:", monitoringUrl);
      
      const newSocket = io(monitoringUrl, {
        transports: ["websocket", "polling"],
        auth: {
          token: parsedToken,
          userId: userId,
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
      });

      // Connection event handlers
      newSocket.on("connect", () => {
        console.log("✅ Socket.IO conectado al namespace /monitoring");
        console.log("   Socket ID:", newSocket.id);
        setConnectionStatus("connected");
        setLoading(false);
      });

      newSocket.on("disconnect", () => {
        console.log("⚠️ Socket.IO desconectado");
        setConnectionStatus("disconnected");
      });

      newSocket.on("connect_error", (error) => {
        console.error("❌ Socket.IO connection error:", error);
        setConnectionStatus("disconnected");
        setLoading(false);
      });

      newSocket.on("reconnecting", () => {
        console.log("🔄 Socket.IO reconectando...");
        setConnectionStatus("reconnecting");
      });

      // Metrics event handlers - USAR EL EVENTO CORRECTO
      newSocket.on("monitoring:metrics", (metrics) => {
        console.log("📊 Métricas recibidas:", metrics);
        setCurrentMetrics(metrics);
        
        // Add to history for charts
        setMetricsHistory((prev) => {
          const newHistory = [
            ...prev,
            {
              timestamp: new Date().toLocaleTimeString(),
              cpu: metrics.system?.cpu?.usage || 0,
              memory: metrics.system?.memory?.usagePercent || 0,
              disk: metrics.system?.disk?.volumes?.[0]?.usagePercent || 0,
              heapUsed: ((metrics.nodeProcess?.memory?.heapUsed || 0) / 1024 / 1024).toFixed(0),
              eventLoopLag: metrics.nodeProcess?.eventLoop?.lag || 0,
              dbConnections: metrics.database?.connections?.active || 0,
            },
          ];
          
          // Keep only last maxDataPoints
          if (newHistory.length > maxDataPoints) {
            return newHistory.slice(newHistory.length - maxDataPoints);
          }
          
          return newHistory;
        });
      });

      newSocket.on("monitoring:error", (error) => {
        console.error("❌ Error en métricas:", error);
        toast.error(error.message || "Error al obtener métricas");
      });

      setSocket(newSocket);
    };

    initSocket();

    // Cleanup on unmount
    return () => {
      if (socket) {
        console.log("🔌 Desconectando socket...");
        socket.close();
      }
    };
  }, []);

  const getStatusColor = () => {
    switch (connectionStatus) {
      case "connected":
        return classes.connected;
      case "disconnected":
        return classes.disconnected;
      case "reconnecting":
        return classes.reconnecting;
      default:
        return classes.disconnected;
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case "connected":
        return "Conectado";
      case "disconnected":
        return "Desconectado";
      case "reconnecting":
        return "Reconectando...";
      default:
        return "Desconectado";
    }
  };

  if (loading) {
    return (
      <Container className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4">Mi Servidor</Typography>
            <Typography variant="body2" color="textSecondary">
              Monitoreo en tiempo real del servidor
            </Typography>
          </Grid>
          <Grid item>
            <Box className={classes.connectionStatus}>
              <Box className={`${classes.statusIndicator} ${getStatusColor()}`} />
              <Typography variant="body2">{getStatusText()}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Metrics Display */}
      {currentMetrics ? (
        <Grid container spacing={3}>
          {/* CPU Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">CPU</Typography>
              <Typography variant="h3">
                {currentMetrics.system?.cpu?.usage?.toFixed(1) || 0}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {currentMetrics.system?.cpu?.cores || 0} núcleos
              </Typography>
            </Paper>
          </Grid>

          {/* Memory Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Memoria RAM</Typography>
              <Typography variant="h3">
                {currentMetrics.system?.memory?.usagePercent?.toFixed(1) || 0}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {((currentMetrics.system?.memory?.used || 0) / 1024 / 1024 / 1024).toFixed(2)} GB / {" "}
                {((currentMetrics.system?.memory?.total || 0) / 1024 / 1024 / 1024).toFixed(2)} GB
              </Typography>
            </Paper>
          </Grid>

          {/* Disk Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Disco</Typography>
              <Typography variant="h3">
                {currentMetrics.system?.disk?.volumes?.[0]?.usagePercent?.toFixed(1) || 0}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {((currentMetrics.system?.disk?.volumes?.[0]?.used || 0) / 1024 / 1024 / 1024).toFixed(2)} GB / {" "}
                {((currentMetrics.system?.disk?.volumes?.[0]?.total || 0) / 1024 / 1024 / 1024).toFixed(2)} GB
              </Typography>
            </Paper>
          </Grid>

          {/* Uptime */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Tiempo Activo</Typography>
              <Typography variant="h3">
                {Math.floor((currentMetrics.system?.uptime || 0) / 86400)}d
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {Math.floor(((currentMetrics.system?.uptime || 0) % 86400) / 3600)}h {" "}
                {Math.floor(((currentMetrics.system?.uptime || 0) % 3600) / 60)}m
              </Typography>
            </Paper>
          </Grid>

          {/* Node.js Process Metrics */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Node.js Heap</Typography>
              <Typography variant="h3">
                {((currentMetrics.nodeProcess?.memory?.heapUsed || 0) / 1024 / 1024).toFixed(0)} MB
              </Typography>
              <Typography variant="body2" color="textSecondary">
                de {((currentMetrics.nodeProcess?.memory?.heapTotal || 0) / 1024 / 1024).toFixed(0)} MB
              </Typography>
            </Paper>
          </Grid>

          {/* Event Loop Lag */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Event Loop Lag</Typography>
              <Typography variant="h3">
                {currentMetrics.nodeProcess?.eventLoop?.lag?.toFixed(2) || 0} ms
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {currentMetrics.nodeProcess?.eventLoop?.lag > 100 ? "⚠️ Alto" : "✓ Normal"}
              </Typography>
            </Paper>
          </Grid>

          {/* Database Connections */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Base de Datos</Typography>
              <Typography variant="h3">
                {currentMetrics.database?.connections?.active || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {currentMetrics.database?.pool?.inUse || 0} / {currentMetrics.database?.pool?.size || 0} en uso
              </Typography>
            </Paper>
          </Grid>

          {/* Redis Status */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper className={classes.metricCard}>
              <Typography variant="h6">Redis</Typography>
              {currentMetrics.redis ? (
                <>
                  <Typography variant="h3">
                    {currentMetrics.redis.connected ? "✓" : "✗"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {currentMetrics.redis.keys?.total || 0} claves
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Redis no disponible
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* CPU & Memory Chart */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartCard}>
              <Typography variant="h6" gutterBottom>
                CPU y Memoria RAM (%)
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="cpu" 
                    stroke="#8884d8" 
                    name="CPU (%)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="memory" 
                    stroke="#82ca9d" 
                    name="RAM (%)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Node.js Heap & Event Loop Chart */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartCard}>
              <Typography variant="h6" gutterBottom>
                Node.js Heap (MB) y Event Loop Lag (ms)
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="heapUsed" 
                    stroke="#ff7300" 
                    name="Heap (MB)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="eventLoopLag" 
                    stroke="#ff0000" 
                    name="Event Loop Lag (ms)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Database Connections Chart */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartCard}>
              <Typography variant="h6" gutterBottom>
                Conexiones de Base de Datos
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="dbConnections" 
                    stroke="#8b5cf6" 
                    name="Conexiones Activas"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Disk Usage Chart */}
          <Grid item xs={12} md={6}>
            <Paper className={classes.chartCard}>
              <Typography variant="h6" gutterBottom>
                Uso de Disco (%)
              </Typography>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={metricsHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    tick={{ fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="disk" 
                    stroke="#f59e0b" 
                    name="Disco (%)"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      ) : (
        <Paper className={classes.metricCard}>
          <Typography variant="body1" color="textSecondary" align="center">
            Esperando métricas del servidor...
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default ServerMonitoring;
